import React, { useMemo } from "react"
import { graphql, PageProps } from "gatsby"
import { useQuery } from "@apollo/client"

import { useConfigContext } from "@app/providers/config"
import { useCore } from "@app/hooks/useCore"
import { useShopify } from "@app/hooks/useShopify"
import { Product as Template } from "@app/components/Product/Product"

import type { ProductProps, ProductCombinedProps } from "@root/types/global"
import { useCartContext } from "@app/providers/cart"

export type Props = PageProps<GatsbyTypes.TemplateProductQuery, GatsbyTypes.TemplateProductQueryVariables> & {
  loading: boolean
  product: ProductCombinedProps
}

export const query = graphql`
  query TemplateProduct($handle: String!, $groupQuery1: [String!], $groupQuery2: [String!]) {
    product: sanityProduct(shopify: { handle: { eq: $handle }, deleted: { ne: true }, published: { eq: true } }) {
      ...SanityProductFragment
      metadata: _rawMetadata(resolveReferences: { maxDepth: 2 })
    }
    grouped: allSanityProduct(filter: { shopify: { published: { eq: true }, deleted: { ne: true } }, tags: { in: $groupQuery1 } }) {
      edges {
        node {
          ...SanityProductFragment
        }
      }
    }
    swatches: allSanityProduct(filter: { shopify: { published: { eq: true }, deleted: { ne: true } }, tags: { in: $groupQuery2 } }) {
      edges {
        node {
          ...SanityProductFragment
        }
      }
    }
    pairings: allSanityProductPairings(
      filter: { products: { elemMatch: { shopify: { published: { eq: true }, deleted: { eq: false }, handle: { eq: $handle } } } } }
    ) {
      edges {
        node {
          products {
            ...SanityProductFragment
          }
        }
      }
    }
    curations: allSanityProductCurations(
      filter: { products: { elemMatch: { shopify: { published: { eq: true }, deleted: { eq: false }, handle: { eq: $handle } } } } }
    ) {
      edges {
        node {
          image: _rawImage(resolveReferences: { maxDepth: 2 })
          link: _rawLink(resolveReferences: { maxDepth: 2 })
          imagePretitle
          imageTitle
          sectionDescription
          products {
            ...SanityProductFragment
          }
        }
      }
    }
    lookbook: allSanityLookBookProduct(
      filter: { parentProduct: { shopify: { published: { eq: true }, deleted: { eq: false }, handle: { eq: $handle } } } }
    ) {
      edges {
        node {
          parentProduct {
            ...SanityProductFragment
          }
          image: _rawImage(resolveReferences: { maxDepth: 2 })
          hideMobileImage
          products {
            ...SanityProductFragment
          }
        }
      }
    }
    template: sanityTemplateProduct {
      metadata: _rawMetadata(resolveReferences: { maxDepth: 2 })
      additionalRelated
      settingEnableReviews
    }
  }
  fragment SanityProductFragment on SanityProduct {
    id
    url
    title
    descriptionHtml
    image
    sanityImages: images
    tags
    related {
      shopify {
        handle
      }
    }
    shopify {
      id
      handle
      deleted
      published
      raw
    }
    care {
      care_instructions
    }
    composition {
      composition
    }
    tagline {
      tagline
    }
  }
`

const Component: React.FC<Props> = ({ data, ...props }) => {
  const {
    helpers: { edgeNormaliser },
    graphql: {
      queries: { GET_PRODUCT_AVAILABILITY },
    },
  } = useCore()
  const { productNormaliser, variantsNormaliser } = useShopify()
  const { countryCode } = useCartContext()
  const {
    settings: {
      product: { filterSortOrder },
    },
  } = useConfigContext()
  const {
    product: {
      // @ts-ignore
      shopify: { handle, raw },
      ...sanityProduct
    },
    swatches: rawSwatches,
    grouped: rawGrouped,
    pairings: rawPairings,
    curations: rawCurations,
    lookbook: rawLookbook,
  } = data

  let rawProduct = undefined
  try {
    rawProduct = { ...sanityProduct, ...JSON.parse(raw), images: sanityProduct.sanityImages }
  } catch (err) {
    console.error("Error parsing raw shopify product", (err as Error).message)
  }

  /*
    Grouped, requires a config setting to set the tag, so if this is returning empty when it shouldnt check the configs.
    Check gatsby-node aswell as the config might need to be declared in the settings.
  */
  const grouped = edgeNormaliser(rawGrouped)
    .map((item: GatsbyTypes.SanityProductFragmentFragment) => {
      let rawProduct = undefined
      try {
        const parsedProduct = JSON.parse(item?.shopify?.raw)
        rawProduct = item?.shopify?.raw ? { ...parsedProduct } : undefined
      } catch (err) {
        console.error("Error parsing raw grouped shopify product", (err as Error).message)
      }

      return rawProduct
    })
    .filter((item?: ProductProps) => item)
    .sort(
      ({ variants: variantsA }: ProductProps, { variants: variantsB }: ProductProps) =>
        filterSortOrder.indexOf(variantsA?.[0]?.title) - filterSortOrder.indexOf(variantsB?.[0]?.title)
    )

  /*
    Swatches, requires a config setting to set the tag, so if this is returning empty when it shouldnt check the configs.
    Check gatsby-node aswell as the config might need to be declared in the settings.
  */
  const swatches = edgeNormaliser(rawSwatches)
    .map((item: GatsbyTypes.SanityProductFragmentFragment) => {
      let rawProduct = undefined
      try {
        const parsedProduct = JSON.parse(item?.shopify?.raw)
        rawProduct = item?.shopify?.raw ? { ...parsedProduct } : undefined
      } catch (err) {
        console.error("Error parsing raw grouped shopify product", (err as Error).message)
      }

      return rawProduct
    })
    .filter((item?: ProductProps) => item)

  const {
    data: live,
    error,
    loading,
  } = useQuery(GET_PRODUCT_AVAILABILITY, {
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
    variables: { countryCode, handle },
    skip: !handle,
  })

  const pairings = edgeNormaliser(rawPairings)
    .reduce((acc, curr) => {
      if (!curr.products || curr.products.length === 0) return acc
      return acc.concat(curr.products)
    }, [])
    .map((item: GatsbyTypes.SanityProductFragmentFragment) => {
      let rawProduct = undefined
      try {
        const parsedProduct = JSON.parse(item?.shopify?.raw)
        rawProduct = item?.shopify?.raw ? { ...parsedProduct } : undefined
      } catch (err) {
        console.error("Error parsing raw paired shopify product", (err as Error).message)
      }

      return productNormaliser(rawProduct)
    })
    .filter((item?: ProductProps) => item && item.handle !== handle)

  const normalisedCurations = edgeNormaliser(rawCurations)

  const curationProducts = [...normalisedCurations]
    .reduce((acc, curr) => {
      if (!curr.products || curr.products.length === 0) return acc
      return acc.concat(curr.products)
    }, [])
    .map((item: GatsbyTypes.SanityProductFragmentFragment) => {
      let rawProduct = undefined
      try {
        const parsedProduct = JSON.parse(item?.shopify?.raw)
        rawProduct = item?.shopify?.raw ? { ...parsedProduct } : undefined
      } catch (err) {
        console.error("Error parsing raw curation shopify product", (err as Error).message)
      }

      return productNormaliser(rawProduct)
    })
    .filter((item?: ProductProps) => item && item.handle !== handle)

  let curations = undefined

  if (curationProducts.length > 0) {
    curations = { ...normalisedCurations[0], products: curationProducts }
  }

  if (error) console.error(error)

  const normalisedLookbook = edgeNormaliser(rawLookbook)

  const lookbookProducts = [...normalisedLookbook]
    .reduce((acc, curr) => {
      if (!curr.products || curr.products.length === 0) return acc
      return acc.concat(curr.products)
    }, [])
    .map((item: GatsbyTypes.SanityProductFragmentFragment) => {
      let rawProduct = undefined
      try {
        const parsedProduct = JSON.parse(item?.shopify?.raw)
        rawProduct = item?.shopify?.raw ? { ...parsedProduct } : undefined
      } catch (err) {
        console.error("Error parsing raw lookbook shopify product", (err as Error).message)
      }

      return productNormaliser(rawProduct)
    })

  let lookbook = undefined

  if (lookbookProducts.length > 0) {
    lookbook = { ...normalisedLookbook[0], products: lookbookProducts }
  }

  const liveProduct = useMemo(() => {
    try {
      return {
        ...(rawProduct ? productNormaliser(rawProduct) : {}),
        ...(live?.product?.variants
          ? {
              variants: rawProduct?.variants.map((variant: any, index: number) => ({
                ...variant,
                ...variantsNormaliser(live.product.variants)[index],
              })),
            }
          : {}),
      }
    } catch (err) {
      console.error("Error parsing live shopify product", (err as Error).message)
      return undefined
    }
  }, [live, rawProduct, productNormaliser, variantsNormaliser])

  return (
    <Template
      {...props}
      {...data}
      grouped={grouped}
      swatches={swatches}
      loading={loading}
      product={liveProduct || rawProduct}
      pairings={pairings}
      curations={curations}
      lookbook={lookbook}
    />
  )
}

export default Component
